/**
 * Utilitários para parsing e cálculo de lag/lead em dependências de tarefas
 *
 * Sintaxe suportada:
 * - "+2d" ou "2d" - Lag de 2 dias úteis
 * - "-1d" - Lead de 1 dia (antecipação)
 * - null/undefined - Usa lag padrão (+1d)
 */

import { addDays } from './dateUtils';

// Lag padrão quando não especificado (mantém comportamento atual)
export const DEFAULT_LAG: string = '+1d';

// Regex para parsing de lag: opcional +/-, número, e "d" para dias
const LAG_REGEX: RegExp = /^([+-])?(\d+)d$/i;

interface ParsedLag {
  value: number;
}

interface ParsedPredecessor {
  rowNum: number;
  lag: string | null;
}

interface LagSettings {
  workDays?: number[];
  holidays?: string[];
}

/**
 * Parse lag string para objeto
 * @param {string|null|undefined} lagStr - Ex: "+2d", "-1d", "3d"
 * @returns {{ value: number }} ou null se inválido
 */
export const parseLag = (lagStr: string | null | undefined): ParsedLag | null => {
  if (!lagStr || typeof lagStr !== 'string') {
    return null;
  }

  const trimmed = lagStr.trim();
  const match = trimmed.match(LAG_REGEX);

  if (!match) {
    return null;
  }

  const sign = match[1] === '-' ? -1 : 1;
  const value = parseInt(match[2], 10) * sign;

  return { value };
};

/**
 * Formata valor de lag para string
 * @param {number} value - Valor do lag (positivo ou negativo)
 * @returns {string} - Ex: "+2d", "-1d"
 */
export const formatLag = (value: number): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return DEFAULT_LAG;
  }

  if (value >= 0) {
    return `+${value}d`;
  }
  return `${value}d`;
};

/**
 * Valida sintaxe de lag
 * @param {string|null|undefined} lagStr
 * @returns {boolean}
 */
export const isValidLag = (lagStr: string | null | undefined): boolean => {
  if (!lagStr || typeof lagStr !== 'string') {
    return false;
  }
  return LAG_REGEX.test(lagStr.trim());
};

/**
 * Normaliza lag string (adiciona + se não tiver sinal)
 * @param {string|null|undefined} lagStr
 * @returns {string} - Lag normalizado ou DEFAULT_LAG
 */
export const normalizeLag = (lagStr: string | null | undefined): string => {
  const parsed = parseLag(lagStr);
  if (!parsed) {
    return DEFAULT_LAG;
  }
  return formatLag(parsed.value);
};

/**
 * Verifica se a data é um dia útil
 * @param {Date} date
 * @param {number[]} workDays - Dias da semana que são úteis (0=Dom, 1=Seg, etc)
 * @param {string[]} holidays - Array de datas de feriados
 * @returns {boolean}
 */
const isWorkDay = (date: Date, workDays: number[], holidays: string[]): boolean => {
  const dayOfWeek = date.getDay();
  if (!workDays.includes(dayOfWeek)) {
    return false;
  }

  // Verificar se é feriado
  if (holidays && holidays.length > 0) {
    const dateStr = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    const dateStrShort = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getFullYear()).slice(-2)}`;

    return !holidays.some(h => h === dateStr || h === dateStrShort);
  }

  return true;
};

/**
 * Encontra o próximo dia útil a partir de uma data
 * @param {Date} date
 * @param {number[]} workDays
 * @param {string[]} holidays
 * @returns {Date}
 */
const getNextWorkDay = (date: Date, workDays: number[], holidays: string[]): Date => {
  const result = new Date(date);
  while (!isWorkDay(result, workDays, holidays)) {
    result.setDate(result.getDate() + 1);
  }
  return result;
};

/**
 * Encontra o dia útil anterior a partir de uma data
 * @param {Date} date
 * @param {number[]} workDays
 * @param {string[]} holidays
 * @returns {Date}
 */
const getPreviousWorkDay = (date: Date, workDays: number[], holidays: string[]): Date => {
  const result = new Date(date);
  while (!isWorkDay(result, workDays, holidays)) {
    result.setDate(result.getDate() - 1);
  }
  return result;
};

/**
 * Adiciona N dias úteis a uma data
 * @param {Date} date
 * @param {number} workDaysToAdd - Pode ser negativo para subtrair
 * @param {number[]} workDays
 * @param {string[]} holidays
 * @returns {Date}
 */
const addWorkDays = (date: Date, workDaysToAdd: number, workDays: number[], holidays: string[]): Date => {
  const result = new Date(date);
  const direction = workDaysToAdd >= 0 ? 1 : -1;
  let remaining = Math.abs(workDaysToAdd);

  while (remaining > 0) {
    result.setDate(result.getDate() + direction);
    if (isWorkDay(result, workDays, holidays)) {
      remaining--;
    }
  }

  return result;
};

/**
 * Aplica lag a uma data de término do predecessor
 *
 * Lógica:
 * - Lag positivo (+2d): Início é 2 dias úteis APÓS o término do predecessor
 * - Lag zero (+0d): Início é o próximo dia útil após o término
 * - Lag negativo (-1d): Início é 1 dia útil ANTES do término do predecessor (lead/overlap)
 *
 * @param {Date} predecessorEndDate - Data de término do predecessor
 * @param {string|null} lagStr - Ex: "+2d", "-1d", ou null para padrão
 * @param {Object} settings - { workDays: number[], holidays: string[] }
 * @returns {Date} - Nova data de início para a tarefa dependente
 */
export const applyLag = (predecessorEndDate: Date, lagStr: string | null, settings: LagSettings): Date => {
  const { workDays = [1, 2, 3, 4, 5], holidays = [] } = settings || {};

  // Parse lag ou usar padrão
  const parsed = parseLag(lagStr) || parseLag(DEFAULT_LAG);
  const lagValue = parsed?.value ?? 1;

  // Começar do dia seguinte ao término do predecessor
  const dayAfterEnd = addDays(predecessorEndDate, 1);

  if (lagValue === 0) {
    // Lag zero: próximo dia útil após o término
    return getNextWorkDay(dayAfterEnd, workDays, holidays);
  } else if (lagValue > 0) {
    // Lag positivo: adicionar dias úteis
    // Primeiro, ir para o próximo dia útil, depois adicionar (lagValue - 1) dias úteis
    const firstWorkDay = getNextWorkDay(dayAfterEnd, workDays, holidays);
    if (lagValue === 1) {
      return firstWorkDay;
    }
    return addWorkDays(firstWorkDay, lagValue - 1, workDays, holidays);
  } else {
    // Lag negativo (lead): a tarefa pode começar ANTES do predecessor terminar
    // lagValue é negativo, então estamos voltando dias úteis a partir do endDate
    // Ex: -1d significa começar 1 dia útil antes do término
    return addWorkDays(predecessorEndDate, lagValue, workDays, holidays);
  }
};

/**
 * Extrai lag de uma string de predecessor (ex: "1 +2d" -> "+2d")
 * @param {string} predecessorStr - Ex: "1 +2d", "1+2d", "1"
 * @returns {{ rowNum: number, lag: string|null }} ou null se inválido
 */
export const parsePredecessorWithLag = (predecessorStr: string | null | undefined): ParsedPredecessor | null => {
  if (!predecessorStr || typeof predecessorStr !== 'string') {
    return null;
  }

  const trimmed = predecessorStr.trim();

  // Regex: número da linha, opcionalmente espaço, opcionalmente lag
  // Exemplos: "1", "1 +2d", "1+2d", "1 -1d"
  const match = trimmed.match(/^(\d+)\s*([+-]?\d+d)?$/i);

  if (!match) {
    return null;
  }

  const rowNum = parseInt(match[1], 10);
  const lag = match[2] || null;

  return { rowNum, lag };
};

/**
 * Formata predecessor com lag para exibição
 * @param {number} rowNum - Número da linha
 * @param {string|null} lag - Lag string ou null
 * @returns {string} - Ex: "1 +2d" ou "1"
 */
export const formatPredecessorWithLag = (rowNum: number, lag: string | null): string => {
  if (!lag || lag === DEFAULT_LAG) {
    // Não mostrar lag padrão para manter exibição limpa
    return String(rowNum);
  }
  return `${rowNum} ${lag}`;
};
